<template>
  <div class="file-upload-container">
    <span v-if="label" class="file-upload-label">{{ label }}</span>
    <div
      class="file-upload-dropzone"
      :class="{
        'has-error': hasError,
        'has-file': file,
        'is-dragging': isDragging
      }"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      @click="triggerFileInput"
      @keydown.enter="triggerFileInput"
      @keydown.space="triggerFileInput"
      tabindex="0"
      role="button"
      :aria-label="placeholder"
    >
      <label class="sr-only" for="file-upload-input">{{ label || 'Upload file' }}</label>
      <input
        id="file-upload-input"
        type="file"
        ref="fileInput"
        :accept="acceptTypes"
        style="display: none"
        @change="onFileChange"
      />
      
      <!-- Default State - No File -->
      <div v-if="!file" class="dropzone-placeholder">
        <v-icon size="64" color="grey lighten-1">{{ uploadIcon }}</v-icon>
        <p class="mt-2 grey--text">{{ placeholder }}</p>
      </div>
      
      <!-- File Selected State -->
      <div v-else class="file-preview">
        <v-icon size="64" color="grey darken-1">{{ fileIcon }}</v-icon>
        <p class="file-name mt-2">{{ file.name }}</p>
        <div class="progress-container">
          <v-progress-linear
            :value="progress"
            :color="progressColor"
            height="8"
            rounded
          />
          <div class="progress-info">
            <span>{{ formatFileSize(file.size) }}</span>
            <span class="ml-2">
              <v-icon v-if="progress === 100" small :color="progressColor">mdi-check-circle</v-icon>
              {{ progress }}%
            </span>
          </div>
        </div>
        <v-btn
          icon
          color="deep-orange"
          class="delete-btn"
          @click.stop="removeFile"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </div>
    </div>
    
    <!-- Hints -->
    <div class="file-upload-hints">
      <small class="grey--text">Format yang didukung: {{ extensionHint }}</small>
      <small class="grey--text">Ukuran maksimal file {{ formatFileSize(maxSize) }}</small>
    </div>
    
    <!-- Error Messages -->
    <div v-if="errorMessages.length > 0" class="error-messages">
      <small v-for="(error, index) in errorMessages" :key="index" class="error-text">
        {{ error }}
      </small>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FileUploadDropzone',
  
  props: {
    value: {
      type: File,
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Drag & drop file atau klik untuk memilih'
    },
    allowedExtensions: {
      type: Array,
      default: () => ['pdf']
    },
    maxSize: {
      type: Number,
      default: 1048576
    },
    uploadIcon: {
      type: String,
      default: 'mdi-cloud-upload'
    },
    fileIcon: {
      type: String,
      default: 'mdi-file-pdf-box'
    },
    progressColor: {
      type: String,
      default: 'teal'
    },
    errors: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      file: null,
      progress: 0,
      isDragging: false,
      internalErrors: []
    };
  },
  
  computed: {
    acceptTypes() {
      const mimeTypes = {
        pdf: 'application/pdf',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        csv: 'text/csv',
        txt: 'text/plain',
        zip: 'application/zip',
        rar: 'application/x-rar-compressed'
      };
      
      return this.allowedExtensions
        .map(ext => mimeTypes[ext.toLowerCase()] || `.${ext}`)
        .join(',');
    },
    
    extensionHint() {
      return this.allowedExtensions.join(', ').toUpperCase();
    },
    
    errorMessages() {
      return [...this.internalErrors, ...this.errors];
    },
    
    hasError() {
      return this.errorMessages.length > 0;
    }
  },
  
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal !== this.file) {
          this.file = newVal;
          if (newVal) {
            this.progress = 100;
          }
        }
      }
    }
  },
  
  methods: {
    triggerFileInput() {
      if (this.disabled) return;
      this.$refs.fileInput.click();
    },
    
    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.handleFile(file);
      }
    },
    
    onDragOver() {
      if (this.disabled) return;
      this.isDragging = true;
    },
    
    onDragLeave() {
      this.isDragging = false;
    },
    
    onDrop(event) {
      if (this.disabled) return;
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.handleFile(file);
      }
    },
    
    handleFile(file) {
      this.internalErrors = [];
      
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const allowedLower = this.allowedExtensions.map(ext => ext.toLowerCase());
      
      if (!allowedLower.includes(fileExtension)) {
        this.internalErrors.push(`Invalid file type. Allowed format: ${this.extensionHint}`);
        return;
      }
      
      if (file.size > this.maxSize) {
        this.internalErrors.push(`File size must not exceed ${this.formatFileSize(this.maxSize)}.`);
        return;
      }
      
      this.file = file;
      this.progress = 0;
      
      this.simulateProgress();
      
      this.$emit('input', file);
      this.$emit('change', file);
    },
    
    simulateProgress() {
      const interval = setInterval(() => {
        if (this.progress < 100) {
          this.progress += 10;
        } else {
          clearInterval(interval);
          this.$emit('upload-complete', this.file);
        }
      }, 50);
    },
    
    removeFile() {
      this.file = null;
      this.progress = 0;
      this.internalErrors = [];
      this.$refs.fileInput.value = '';
      this.$emit('input', null);
      this.$emit('change', null);
      this.$emit('remove');
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    },
    
    reset() {
      this.removeFile();
    },
    
    validate() {
      if (!this.file) {
        this.internalErrors = ['File wajib diupload'];
        return false;
      }
      return this.internalErrors.length === 0;
    }
  }
};
</script>

<style scoped>
.file-upload-container {
  width: 100%;
}

.file-upload-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.file-upload-dropzone {
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fafafa;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-upload-dropzone:hover {
  border-color: #d32f2f;
  background-color: #fff8f5;
}

.file-upload-dropzone.is-dragging {
  border-color: #d32f2f;
  background-color: #fff3ed;
  border-width: 3px;
}

.file-upload-dropzone.has-error {
  border-color: #d32f2f;
}

.file-upload-dropzone.has-file {
  border-style: solid;
  border-color: #e0e0e0;
  cursor: default;
}

.dropzone-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.file-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
}

.file-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  margin-bottom: 12px;
  word-break: break-all;
  max-width: 80%;
  text-align: center;
}

.progress-container {
  width: 60%;
  max-width: 300px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  font-size: 12px;
  color: #666;
}

.delete-btn {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.file-upload-hints {
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  gap: 2px;
}

.error-messages {
  display: flex;
  flex-direction: column;
  margin-top: 4px;
}

.error-text {
  color: #d32f2f;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
